import {Form} from "../../utils/Form";
import {showNotification} from "../../actions";
import {defineMessage} from "react-intl";
import ErrorHelper from "../../utils/ErrorHelper";
import {useEffect, useMemo, useState} from "react";
import {Dialog, TextField} from "@mui/material";
import {
    MultipleLineError,
    DialogActions,
    DialogContent,
    FormattedDialogTitle,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    LoadingIcon,
} from "@dvrt/graphql-widgets";
import CreateAnotherCheckBox from "../CreateAnotherCheckBox";
import {
    DomainIdByNameQueryResult,
    SaveDeviceVariantMutation,
    SaveDeviceVariantMutationVariables,
    useDomainIdByNameLazyQuery,
    useSaveDeviceVariantMutation,
} from "./queries.generated";
import TargetSelector, {Target, TargetType} from "../TargetSelector";
import {FormattedAlert} from "@dvrt/graphql-widgets";

type SaveDialogProps = {
    isOpen: boolean;
    close: () => void;
    onSuccess: () => void;
    initialData?: SaveDeviceVariantMutationVariables;
    domainName?: string;
};

class SaveDeviceVariantForm extends Form<SaveDeviceVariantMutation, SaveDeviceVariantMutationVariables> {
    constructor(initialData: SaveDeviceVariantMutationVariables) {
        super("saveDeviceVariant");
        this.hooks(useSaveDeviceVariantMutation, initialData, (_) =>
            showNotification(
                initialData.id
                    ? defineMessage({id: "device_variant.updated", defaultMessage: "Device variant updated"})
                    : defineMessage({id: "device_variant.created", defaultMessage: "Device variant created"}),
                "success",
            ),
        );

        // FIXME: For some reason, if we change the domainId of the initial data before we create the form,
        // it will not update the initial values of the form, so it will keep the domainId undefined every single time.
        // We should do a big revise of the form system to avoid this kind of problem and the dataSource issue as well.
        useEffect(() => {
            this.newInitialData(initialData);
        }, [initialData]);
    }

    protected override get filteredVariables(): SaveDeviceVariantMutationVariables {
        const copy = structuredClone(this.values);

        if (this.initialData.domainId === undefined && !copy.domainId) {
            delete copy.domainId;
        } else if (this.initialData.domainId === copy.domainId) {
            delete copy.domainId;
        } else if (!copy.domainId) {
            copy.domainId = null;
        }

        return copy;
    }

    override async submit() {
        const res = await super.submit();

        if (res) {
            this.clearCache();
            this.newInitialData(this.values);
        }

        return res;
    }
}

export default ({isOpen, close, onSuccess, initialData: _initialData, domainName}: SaveDialogProps) => {
    const [createAnother, setCreateAnother] = useState(false);
    const [success, setSuccess] = useState(false);
    const [domainsData, setDomainsData] = useState<DomainIdByNameQueryResult["data"] | undefined>(undefined);

    const [domainsQuery] = useDomainIdByNameLazyQuery({variables: {name: domainName}, fetchPolicy: "no-cache"});

    // FIXME: this whole thing is a mess, but I do not think it is worth more time to be spent on it.
    const initialData = useMemo(() => {
        const data = structuredClone(_initialData ?? {name: ""});
        if (domainName && !data.domainId && domainsData) {
            data.domainId = domainsData?.domains.items[0].id;
        } else if (data.domainId == undefined) {
            // needed because of the TargetSelector using 0 as the default value ("all")
            data.domainId = 0;
        }
        return data;
    }, [_initialData, domainName, domainsData]);

    const form = new SaveDeviceVariantForm(initialData);

    useEffect(() => {
        if (isOpen) {
            domainsQuery().then((result) => setDomainsData(result.data));
        }
    }, [domainsQuery, isOpen, domainName]);

    const errorHelper = new ErrorHelper(form.errors);

    useEffect(() => {
        console.log("form errors", form.errors);
    }, [form.errors]);

    async function submit() {
        if (!(await form.submit())) return;

        setSuccess(true);

        if (!createAnother) handleClose(true);
    }

    function handleClose(asyncSuccess?: boolean) {
        const actualSuccess = asyncSuccess === null ? success : asyncSuccess;

        close();
        if (actualSuccess) onSuccess();
        form.reset();
    }

    const closeDialog = () => {
        form.reset();
        close();
    };

    console.log("form values", form.values);

    return (
        <Dialog open={isOpen}>
            <FormattedDialogTitle
                title={
                    initialData.id
                        ? defineMessage({id: "device_variant.update", defaultMessage: "Update device variant"})
                        : defineMessage({id: "device_variant.create", defaultMessage: "New device variant"})
                }
            />
            <DialogContent>
                <LoadingCircleWrapper loading={!domainsData}>
                    <FormContainer>
                        <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})}>
                            <TextField
                                variant="standard"
                                margin="dense"
                                value={form.values.name}
                                onChange={(event) => form.updateValues({name: event.target.value})}
                                error={errorHelper.hasError("name")}
                                helperText={<MultipleLineError errors={errorHelper.fieldErrors("name")} />}
                            />
                            {form.values.id && form.values.name !== initialData.name && (
                                <FormattedAlert
                                    message={defineMessage({
                                        id: "device_variant.update_warnings.rename",
                                        defaultMessage: "Don't forget to rename the template files too",
                                    })}
                                    severity="warning"
                                    fullWidth
                                />
                            )}
                        </FormattedFormRow>
                        <FormattedFormRow label={defineMessage({id: "domain", defaultMessage: "Domain"})}>
                            <TargetSelector
                                selected={form.values.domainId}
                                handleChange={(id) => form.updateValues({domainId: id})}
                                targets={domainsData?.domains.items.map(
                                    (domain): Target => ({
                                        numberId: domain.id,
                                        displayName: domain.name,
                                        shortNumber: null,
                                        type: TargetType.DOMAIN,
                                    }),
                                )}
                                variant="standard"
                                notSelectedText={defineMessage({id: "all", defaultMessage: "All"})}
                                allowWithoutShortNumber
                            />
                            {form.values.id && !initialData.domainId && form.values.domainId && (
                                <FormattedAlert
                                    message={defineMessage({
                                        id: "device_variant.update_warnings.visibility_global_to_domain",
                                        defaultMessage:
                                            "The variant will be created in all domains where it is currently used, including the selected domain.",
                                    })}
                                    severity="warning"
                                    fullWidth
                                />
                            )}
                            {form.values.id && initialData.domainId && !form.values.domainId && (
                                <FormattedAlert
                                    message={defineMessage({
                                        id: "device_variant.update_warnings.visibility_domain_to_global",
                                        defaultMessage:
                                            "All variants with the same name will be merged into one global variant.",
                                    })}
                                    severity="warning"
                                    fullWidth
                                />
                            )}
                        </FormattedFormRow>
                    </FormContainer>
                </LoadingCircleWrapper>
            </DialogContent>
            <DialogActions>
                {form.values.id === undefined && (
                    <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
                )}
                <FormDialogActionButtons
                    mainId="device-variant-save"
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={closeDialog}
                    enabled={form.hasChanged}
                />
            </DialogActions>
        </Dialog>
    );
};

const LoadingCircleWrapper = ({loading, children}: {loading: boolean; children: React.ReactNode}) => {
    if (loading) return <LoadingIcon />;
    else return children;
};
